/**
 * AutoSaveContext — global broadcast channel for auto-save status.
 *
 * The <AutoSaveIndicator /> subscribes to `status` here and renders a Gmail-
 * style pill at the bottom-right of the viewport whenever a save is in
 * flight, has just succeeded, or has failed. Individual forms report status
 * transitions via `useAutoSave` — the hook automatically broadcasts up to
 * this provider when it is mounted in the tree.
 *
 * The hook (`useAutoSaveStatus`) intentionally accepts an `optional` flag so
 * `useAutoSave` can be used in isolation (e.g. unit tests) without wrapping
 * the test tree in a provider.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const AutoSaveContext = createContext(null);

/**
 * useAutoSaveStatus — access global auto-save status.
 *
 * @param {{ optional?: boolean }} opts - when `optional`, returns null
 *   instead of throwing when no provider is mounted. Used by useAutoSave to
 *   degrade gracefully in tests.
 */
export const useAutoSaveStatus = (opts = {}) => {
  const ctx = useContext(AutoSaveContext);
  if (!ctx) {
    if (opts?.optional) return null;
    throw new Error('useAutoSaveStatus must be used within <AutoSaveProvider>');
  }
  return ctx;
};

export const AutoSaveProvider = ({ children }) => {
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [lastError, setLastError] = useState(null);

  const reportError = useCallback((err) => {
    setLastError(err?.message || 'Ukjent feil');
    setStatus('error');
  }, []);

  const clearError = useCallback(() => {
    setLastError(null);
    setStatus('idle');
  }, []);

  const value = useMemo(
    () => ({ status, setStatus, lastError, reportError, clearError }),
    [status, lastError, reportError, clearError],
  );

  return <AutoSaveContext.Provider value={value}>{children}</AutoSaveContext.Provider>;
};

export default AutoSaveProvider;
